/**
 * The new-team dialog (platform design §5.2.1; Figma `325:12066`, dialog `325:12257`): a team
 * goes inside a chosen place first (`ParentPicker`), which decides its level, and takes one
 * lead -- an account or a name, never both, never several (`LeadPicker`). Attaching accounts is
 * not part of this dialog; that happens afterwards, through the team's own `Contas da equipe`.
 *
 * `open`/`onOpenChange` are controlled only when a caller passes them (`TeamActions` opening this
 * prefilled from a team's `⋯` menu); the header's own `Nova equipe` button owns its state
 * uncontrolled, with no `initialParentTeamId`, the same as before.
 */
import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import { Plus } from 'lucide-react';

import { FormDialog } from '../../../chrome/form';
import { fieldErrorOf } from '../../../chrome/form/fieldErrorOf';
import { Button } from '../../../ui/button';
import { Chip } from '../../../ui/chip';
import { Field, FieldError, FieldLabel } from '../../../ui/field';
import { Input } from '../../../ui/input';
import { useCan } from '../../session/orgContext';
import { t } from '../i18n';
import { LeadPicker } from './LeadPicker';
import type { LeadSelection } from './LeadPicker';
import { ParentPicker } from './ParentPicker';
import type { ParentSelection } from './ParentPicker';
import { useStructure, useTeamCreate } from './hooks';
import type { TeamIn } from './schemas';

/** Shared with `EditTeam`, which sends the same two fields off the same picker. */
export function leadFields(
  lead: LeadSelection | null,
): Pick<TeamIn, 'lead_name' | 'lead_account_id'> {
  if (lead === null) return { lead_name: null, lead_account_id: null };
  if (lead.kind === 'name') return { lead_name: lead.name, lead_account_id: null };
  return {
    lead_name: lead.account.label ?? lead.account.handle ?? lead.account.id,
    lead_account_id: lead.account.id,
  };
}

export interface NewTeamProps {
  campaignId: string;
  /** `undefined`: nothing preselected, the reader chooses. `null`: preselects the top ("Sem
   *  equipe acima"). A team id: preselects that team, deriving the level one below it. */
  initialParentTeamId?: string | null;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function NewTeam(props: NewTeamProps): ReactNode {
  const { campaignId, initialParentTeamId } = props;
  const canWrite = useCan('control_plane.write');
  const structureQuery = useStructure(campaignId);
  const createMutation = useTeamCreate(campaignId);

  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = props.open !== undefined;
  const open = isControlled ? (props.open ?? false) : internalOpen;
  const setOpen = isControlled ? (props.onOpenChange ?? (() => {})) : setInternalOpen;

  const [parent, setParent] = useState<ParentSelection | null>(null);
  const [name, setName] = useState('');
  const [lead, setLead] = useState<LeadSelection | null>(null);
  const [parentError, setParentError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);

  const nameId = useId();

  const failure = createMutation.error?.failure ?? null;
  const nameServerError = fieldErrorOf(failure, 'name');

  const levels = [...(structureQuery.data?.levels ?? [])].sort((a, b) => a.position - b.position);
  const teams = structureQuery.data?.teams ?? [];

  // Re-derived every time the dialog opens (and once the structure it reads from has loaded),
  // never carried across a previous open where a different team may have been passed in.
  useEffect(() => {
    if (!open) return;
    if (initialParentTeamId === undefined) {
      setParent(null);
      return;
    }
    if (initialParentTeamId === null) {
      const first = levels[0];
      setParent(first ? { parentTeamId: null, levelId: first.id } : null);
      return;
    }
    const parentTeam = teams.find((team) => team.id === initialParentTeamId);
    const nextLevel = levels.find(
      (level) => level.position === (parentTeam?.level_position ?? -1) + 1,
    );
    setParent(
      parentTeam && nextLevel ? { parentTeamId: parentTeam.id, levelId: nextLevel.id } : null,
    );
  }, [open, initialParentTeamId, structureQuery.data]);

  function reset(): void {
    setName('');
    setLead(null);
    setParentError(null);
    setNameError(null);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void | Promise<unknown> {
    event.preventDefault();
    const trimmedName = name.trim();
    setParentError(parent === null ? t('teams.newTeam.parent.required') : null);
    setNameError(trimmedName === '' ? t('teams.newTeam.name.required') : null);
    if (parent === null || trimmedName === '') return;

    return createMutation.mutateAsync({
      level_id: parent.levelId,
      name: trimmedName,
      parent_team_id: parent.parentTeamId,
      ...leadFields(lead),
    });
  }

  if (!canWrite) return null;

  const derivedLevel =
    parent === null ? undefined : levels.find((level) => level.id === parent.levelId);

  return (
    <FormDialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
      trigger={
        isControlled ? undefined : (
          <Button type="button">
            <Plus aria-hidden className="size-4" />
            {t('teams.newTeam.title')}
          </Button>
        )
      }
      title={t('teams.newTeam.title')}
      submitLabel={t('teams.newTeam.submit')}
      pending={createMutation.isPending}
      failure={failure}
      onSubmit={handleSubmit}
    >
      <Field data-invalid={parentError !== null || undefined}>
        <FieldLabel>{t('teams.newTeam.parent.label')}</FieldLabel>
        <ParentPicker levels={levels} teams={teams} value={parent} onChange={setParent} />
        <FieldError>{parentError}</FieldError>
        {derivedLevel !== undefined && (
          <div className="flex items-center gap-1.5 text-caption text-muted-foreground">
            {t('teams.newTeam.parent.derivedLevel')}
            <Chip size="sm">{derivedLevel.name}</Chip>
          </div>
        )}
      </Field>

      <Field data-invalid={nameError !== null || undefined}>
        <FieldLabel htmlFor={nameId}>{t('teams.newTeam.name.label')}</FieldLabel>
        <Input id={nameId} value={name} onChange={(event) => setName(event.target.value)} />
        <FieldError>{nameError ?? nameServerError}</FieldError>
      </Field>

      <Field>
        <FieldLabel>{t('teams.newTeam.lead.label')}</FieldLabel>
        <LeadPicker campaignId={campaignId} value={lead} onChange={setLead} />
      </Field>
    </FormDialog>
  );
}
